import axios, { AxiosError } from "axios";
import Spinner from "components/Spinner";
import { InformationPageType, redirectToInfomationPage } from "pages/Information";
import React, { useEffect, useRef, useState } from "react";
import { Redirect } from "react-router";
import AuthenticationApi from "../../services/authentication-api";
import OnboardingApi from "../../services/OnboardingApi";

type RedirectFromItsmeState = {
    isLinked: boolean;
    phoneNumber: string;
    oso: string[][];
    acs: string;
    sp: string;
    sessionId: string;
    pi2AuthToken: string;
};

const RedirectFromItsme = (): React.ReactElement => {
    const [redirectingToOnboarding, setRedirectingToOnboarding] = useState(false);
    const [redirectingToSelectOffice, setredirectingToSelectOffice] = useState(false);
    const [redirectingToInformation, setRedirectingToInformation] = useState<InformationPageType | undefined>(
        undefined,
    );

    const handleError = (error: Error | AxiosError): void => {
        if (axios.isAxiosError(error)) {
            if (error.response?.status === 403) {
                setRedirectingToInformation(InformationPageType.NO_LINK_ACCOUNT);
                return;
            }
        }
        setRedirectingToInformation(InformationPageType.GENERIC_ERROR);
    };

    const redirectParams = useRef<RedirectFromItsmeState>({
        isLinked: false,
        phoneNumber: "",
        oso: [],
        acs: "",
        sp: "",
        sessionId: "",
        pi2AuthToken: "",
    });

    const onboardingApi: OnboardingApi = new OnboardingApi();
    const authenticationApi: AuthenticationApi = new AuthenticationApi();

    useEffect(() => {
        const matchingPath = window.location.pathname.split("/");
        const locationSearch = window.location.search.split("&");
        const location = locationSearch.find((p) => p.includes("state"))?.substring(7) ?? "";

        if (matchingPath) {
            const purpose = matchingPath.find((p) => p.includes("onboarding") || p.includes("login")) ?? "";
            if (purpose) {
                if (purpose === "onboarding") {
                    callFItsmePost(location);
                }
                if (purpose === "login") {
                    const queryParams = window.location.search.split("&");
                    let sp = "";
                    let st = "";
                    let acs = "";
                    let params = "";
                    let paramsArray: string[] = [];
                    if (queryParams.find((p) => p.includes("params"))) {
                        params = queryParams.find((p) => p.includes("params"))?.split("params")[1] ?? "";

                        if (params !== "") {
                            paramsArray = params.split("pi2.");
                        }
                    }
                    if (paramsArray.find((p) => p.includes("sp="))) {
                        sp = paramsArray.find((p) => p.includes("sp="))?.split("sp=")[1] ?? "";
                    }
                    if (queryParams.find((p) => p.includes("state"))) {
                        st = queryParams.find((p) => p.includes("state"))?.split("=")[1] ?? "";
                    }
                    if (paramsArray.find((p) => p.includes("acs="))) {
                        acs = paramsArray.find((p) => p.includes("acs="))?.split("acs=")[1] ?? "";
                    }
                    if (sp === "" || st === "" || acs === "") {
                        console.error("Missing parameter");
                    } else {
                        callAuthenticationWithItsme(st, sp, acs);
                    }
                }
            }
        }
    }, []);

    const callFItsmePost = (st: string) => {
        onboardingApi.linkAccount(st).then(
            (response) => {
                redirectParams.current = {
                    ...redirectParams.current,
                    isLinked: response.isLinked,
                    phoneNumber: response.phoneNumber,
                };
                setRedirectingToOnboarding(true);
            },
            (error) => {
                setRedirectingToOnboarding(true);
                throw error;
            },
        );
    };

    const callAuthenticationWithItsme = (st: string, sp: string, acs: string) => {
        authenticationApi
            .authenticateWithItsme(st, sp)
            .then((response) => {
                if (response.officesSubOffices.length > 1) {
                    redirectParams.current = {
                        ...redirectParams.current,
                        oso: response.officesSubOffices,
                        sessionId: response.sessionId,
                        pi2AuthToken: response.pi2AuthToken,
                        acs,
                        sp,
                    };
                    setredirectingToSelectOffice(true);
                } else {
                    authenticationApi
                        .selectOfficeSubOffice({
                            acs,
                            issuer: sp,
                            office: response.officesSubOffices[0][0],
                            pi2AuthToken: response.pi2AuthToken,
                            sessionId: response.sessionId,
                            subOffice: response.officesSubOffices[0][1],
                        })
                        .then((responseSelectOffice) => {
                            if (responseSelectOffice.status === "success") {
                                authenticationApi.autoPost({
                                    acs,
                                    sp,
                                    token: responseSelectOffice.artifact,
                                });
                            } else {
                                console.error("Something went wrong with AutoPost");
                            }
                        })
                        .catch((error) => {
                            handleError(error);
                        });

                    redirectParams.current = {
                        ...redirectParams.current,
                        oso: response.officesSubOffices,
                        sessionId: response.sessionId,
                        pi2AuthToken: response.pi2AuthToken,
                        acs,
                        sp,
                    };
                }
            })
            .catch((error) => {
                handleError(error);
            });
    };

    const redirectToSelectOffice = () => {
        const { oso, acs, sp, sessionId, pi2AuthToken } = redirectParams.current;
        return (
            <Redirect
                to={{
                    pathname: "/select-office",
                    state: {
                        oso,
                        acs,
                        spFull: sp,
                        sessionId,
                        pi2AuthToken,
                    },
                }}
            />
        );
    };

    const redirectToOnboarding = () => {
        const { isLinked, phoneNumber } = redirectParams.current;
        return (
            <Redirect
                to={{
                    pathname: "/onboarding",
                    state: {
                        status: isLinked ? "link" : "notlink",
                        phoneNumber,
                    },
                }}
            />
        );
    };

    if (redirectingToInformation !== undefined) {
        return redirectToInfomationPage(redirectingToInformation);
    }

    if (redirectingToOnboarding) {
        return redirectToOnboarding();
    }

    if (redirectingToSelectOffice) {
        return redirectToSelectOffice();
    }

    return <Spinner isLoading />;
};

export default RedirectFromItsme;
